import { useState } from 'react';
import { camelCase, capitalize } from 'lodash';
import { useJobTaskApi } from '../../../../../clients/quipli-service/job-tasks/job-task-api';
import { ChecklistItem } from '../../../../../clients/quipli-service/job-tasks/job-tasks';
import { useOrderMedia } from '../../../../order/hooks/use-order-media';
import { InspectionModalNav, isTaskComplete, MediaSection, TaskProps } from '../../inspection-modal/inspection-modal';
import { toast } from '../../../../../util/toasts';
import { Typography } from '../../../../../components/typography/typography';
import { InspectionUnitHeader } from '../inspection-header';
import classNames from 'classnames';
import Icon from '../../../../../components/icons/icon';
import { TextArea } from '../../../../../components/textarea/textarea';
import '../../inspection-modal/inspection-modal.scss';
import { OrderMedia } from '../../../../../clients/quipli-service/order/order';

/**
 * Checklist task.
 * @param task - The task to inspect.
 * @param jobId - The job id.
 * @param product - The product to inspect.
 * @param unit - The unit to inspect.
 * @param unitTrackingVia - The unit tracking via to use.
 * @param next - The next function.
 * @param back - The back function.
 * @param isPreview - Whether the task is in preview mode (in the edit view)
 * @param setJob - The function to set the job.
 * @param order - The order to inspect.
 * @param isFirstTask - Whether this is the first task in the inspection.
 * @param isLastTask - Whether this is the last task in the inspection.
 */
export const ChecklistTask = ({
  task,
  jobId,
  product,
  unit,
  unitTrackingVia,
  next,
  back,
  isPreview = false,
  setJob,
  order,
  isFirstTask = false,
  isLastTask = false,
}: TaskProps) => {
  const { updateTask } = useJobTaskApi();
  const { isOrderMediaLoading, mediaList, fetch, upload, isUploading } = useOrderMedia(order);

  const [isSaving, setIsSaving] = useState(false);

  const uploadInspection = async (item: ChecklistItem, section: string, files: File[]) => {
    if (order === undefined) return;

    if (isPreview || files.length === 0 || isUploading || isOrderMediaLoading) return;

    const newMediaIds: string[] = [];

    setIsSaving(true);

    await upload(files, `Unit ${unit[unitTrackingVia]} - ${capitalize(section)} | `, async (mediaId: string) => {
      newMediaIds.push(mediaId);
    });

    const updatedJob = await updateTask(jobId, task.id, 'InProgress', {
      ...task.metadata,
      checklist: task.metadata!.checklist.map((i: ChecklistItem) =>
        i.text === item.text ? { ...i, media: [...(item.media ?? []), ...newMediaIds] } : i,
      ),
    });
    setJob(updatedJob);

    setIsSaving(false);
  };

  const [checklistValues, setChecklistValues] = useState<{ [key: number]: { value?: boolean; notes?: string } }>(
    // initialize dicitionary with checklist item index mapped to item's value and notes
    task.metadata?.checklist.reduce(
      (acc: { [key: number]: { value?: boolean; notes?: string } }, item: ChecklistItem, i: number) => ({
        ...acc,
        [i]: { value: item.value, notes: item.notes },
      }),
      {} as { [key: number]: { value?: boolean; notes?: string } },
    ),
  );

  const saveTask = async (forward: boolean) => {
    if (isPreview || !task || !task.metadata) return;
    try {
      const updatedJob = await updateTask(jobId, task.id, isTaskComplete(task, product), {
        ...task.metadata,
        status: isTaskComplete(task, product) ? 'Complete' : 'InProgress',
        checklist: task.metadata.checklist.map((item: ChecklistItem, i: number) => ({
          ...item,
          value: checklistValues[i].value,
          notes: checklistValues[i].notes,
        })),
      });
      setJob(updatedJob);

      if (forward) next();
      else back();
    } catch (e) {
      console.error(e);
      toast('Error updating task', 'error');
    }
  };

  return (
    <div className="inspection-checklist gap-2">
      <Typography className="text-center" type="text-lg">
        {task.header}
      </Typography>
      <InspectionUnitHeader product={product} unitNumber={unit[unitTrackingVia]} />
      <Typography type="text-sm" className="text-grey-500 mb-2">
        {task.instructions}
      </Typography>

      {task.metadata?.checklist &&
        task.metadata.checklist.map((item: ChecklistItem, idx: number) => {
          const section = `${item.text} - failure images`;

          return (
            <div className="checklist-item-wrapper">
              <div className="flex justify-between">
                <Typography type="text-sm" weight="semi-bold">
                  {item.text}
                </Typography>
                <YesNoToggle
                  value={checklistValues[idx]?.value}
                  idx={idx}
                  checklistValues={checklistValues}
                  setChecklistValues={setChecklistValues}
                />
              </div>
              {/* Any failures? If so, show the failure details section */}
              {checklistValues[idx]?.value === false && (
                <FailureDetailsSection
                  checklistValues={checklistValues}
                  setChecklistValues={setChecklistValues}
                  mediaList={mediaList}
                  isOrderMediaLoading={isOrderMediaLoading}
                  fetch={fetch}
                  uploadInspection={uploadInspection}
                  idx={idx}
                  item={item}
                  section={section}
                  isSaving={isSaving}
                />
              )}
            </div>
          );
        })}
      {!isPreview && (
        <InspectionModalNav isSaving={isSaving} saveTask={saveTask} isFirstStep={isFirstTask} isLastStep={isLastTask} />
      )}
    </div>
  );
};

// TODO: far too many props here, refactor further next time
const FailureDetailsSection = ({
  checklistValues,
  setChecklistValues,
  mediaList,
  isOrderMediaLoading,
  fetch,
  uploadInspection,
  idx,
  item,
  section,
  isSaving,
}: {
  checklistValues: { [key: number]: { value?: boolean; notes?: string } };
  setChecklistValues: (values: { [key: number]: { value?: boolean; notes?: string } }) => void;
  mediaList: OrderMedia[];
  isOrderMediaLoading: boolean;
  fetch: () => void;
  uploadInspection: (item: ChecklistItem, section: string, files: File[]) => void;
  idx: number;
  item: ChecklistItem;
  section: string;
  isSaving: boolean;
}) => {
  return (
    <div className="mb-2">
      <p className="ml-2 text-sm text-grey-500">Provide details</p>
      <TextArea
        value={checklistValues[idx]?.notes}
        onChange={(e) => setChecklistValues({ ...checklistValues, [idx]: { value: false, notes: e.target.value } })}
        className="ml-2 w-full p-2 border border-grey-200 rounded"
      />

      <div>
        <p className="ml-2 text-sm text-grey-500">Upload photos</p>
        {mediaList !== undefined && (
          <MediaSection
            list={mediaList.filter((m) => item.media.includes(m.id))}
            isListLoading={isOrderMediaLoading}
            refresh={fetch}
            isSaving={isSaving}
            upload={(files) => uploadInspection(item, camelCase(section), files)}
            section={section}
            enableControls={true}
          />
        )}
      </div>
    </div>
  );
};

const YesNoToggle = ({
  value,
  idx,
  checklistValues,
  setChecklistValues,
}: {
  value: boolean | undefined;
  idx: number;
  checklistValues: { [key: number]: { value?: boolean; notes?: string } };
  setChecklistValues: (values: { [key: number]: { value?: boolean; notes?: string } }) => void;
}) => {
  return (
    <div className="checklist-yes-no flex items-center gap-3">
      <div
        className={classNames('bg-grey-100 hover:bg-green-100 rounded-full thumbs-up', {
          selected: checklistValues[idx]?.value,
        })}
        onClick={() => setChecklistValues({ ...checklistValues, [idx]: { value: true, notes: '' } })}
      >
        <Icon
          icon="ThumbsUp"
          stroke={checklistValues[idx]?.value ? 'green' : 'grey'}
          color={checklistValues[idx]?.value ? 'green' : 'grey'}
        />
      </div>
      <div
        className={classNames('bg-grey-100 hover:bg-red-100 rounded-full thumbs-down', {
          selected: checklistValues[idx]?.value === false,
        })}
        onClick={() =>
          setChecklistValues({
            ...checklistValues,
            [idx]: { value: false, notes: checklistValues[idx]?.notes ?? '' },
          })
        }
      >
        <Icon
          icon="ThumbsDown"
          stroke={checklistValues[idx]?.value === false ? 'red' : 'grey'}
          color={value === false ? 'red' : 'grey'}
        />
      </div>
    </div>
  );
};
